import { useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ICreateOrder } from "../types/CreateOrder";

import { basketAPI } from "../api/basketAPI";
import { custonSooner } from "../../../components/CustopmSooner";
import { useNavigate } from "react-router-dom";

import { useClearBasket } from "./clearBasket";
import { clearStoredOrderExtras } from "../libs/orderExtrasStorage";
import { activeOrdersCountQueryKey } from "../../orders/services/getActiveOrdersCount";
import { logPurchase } from "../../../lib/firebase";
import { getPaymentUrl } from "../libs/paymentLink";
import { openExternalLink } from "../../../utils/telegramLink";

export type CreateOrderInput = ICreateOrder & {
  analytics?: {
    /** Human-readable payment label ("card", "cash"), not sent to the API. */
    payment_type?: string;
  };
};

export const useCreateOrder = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { clearBasket } = useClearBasket();
  // Заказ уже отправлен и ответа ещё нет. Ref, а не isPending: isPending
  // доходит до кнопки только со следующим рендером, и быстрый двойной тап
  // успевал отправить два заказа.
  const inFlight = useRef(false);
  const { mutateAsync, isPending } = useMutation({
    // По умолчанию без сети мутация не падает, а встаёт на паузу, и кнопку
    // можно жать дальше: каждое нажатие ставит в очередь ещё один заказ, и
    // при появлении интернета они уходят все разом. Отправляем сразу — без
    // сети запрос упадёт, и пользователь увидит ошибку.
    networkMode: "always",
    // `analytics` carries data the purchase event needs but the order endpoint
    // has no field for (payment_type is only known as a label in the UI), so it
    // is stripped back off before the request goes out.
    mutationFn: async (input: CreateOrderInput) => {
      const { analytics, ...body } = input;
      void analytics;
      return await basketAPI.createOrder(body);
    },
    onSuccess: async (res, { analytics, ...body }) => {
      if (!res.data.success) {
        // Оба поля необязательные: у заказа, отклонённого без текста, тост
        // иначе показал бы пустую плашку.
        custonSooner.error(
          res.data.message ?? res.data.title ?? "Что то пошло не так",
        );
        return;
      }
      custonSooner.success("Заказ успешно создан");

      // Онлайн-оплата: платёж в Multicard завёл бэкенд, нам осталось показать
      // его страницу. Открываем первым делом и обязательно через Telegram —
      // системный браузер увёл бы пользователя из мини-аппа, и вернуться к
      // заказу он смог бы только руками. Аналитика и очистка корзины ниже
      // никуда не денутся: openLink мини-апп не закрывает.
      const paymentUrl = getPaymentUrl(res.data);

      // Ссылку возвращает бэкенд, и полей под неё три. Если оплата не
      // открылась — виден весь ответ целиком, а не только то, что мы сумели из
      // него достать. В прод-сборку не попадает.
      if (import.meta.env.DEV) {
        console.info("[order/create]", {
          paymentId: body.payment_id,
          cardId: body.card_id,
          paymentUrl,
          response: res.data,
        });
      }

      if (paymentUrl) {
        openExternalLink(paymentUrl);
      }

      clearBasket({});
      // Выбор приборов/салфеток относится к конкретному заказу, а не к
      // пользователю: он уже уехал в consumables, дальше не переносится.
      clearStoredOrderExtras();

      const orderId = res.data.order?.id;

      if (orderId == null) {
        console.error("[firebase] order id missing, skipping purchase event");
      } else {
        const shipping = body.deliveryCost ?? 0;

        logPurchase({
          transaction_id: String(orderId),
          value: (body.totalPrice ?? 0) - shipping,
           shipping,
          affiliation: "EVOS Telegram",
          coupon: body.promoCode || undefined,
          items: body.positions.map((position) => ({
            item_id: String(position.id),
            item_name: position.title ?? String(position.id),
            price: Number(position.price) || 0,
            quantity: position.amount,
          })),

          order_id: orderId,
          // Formats mirror what the existing apps already send, so the new web
          // events land in the same reports instead of splitting them.
          event_id: `purchase_${orderId}`,
          content_ids: body.positions.map((position) => position.id).join(","),
          content_name: `Order #${orderId}`,
          content_type: "product",
          delivery_type: body.type,
          delivery_cost: shipping,
          bonus_pay: body.bonusPay ?? 0,
          payment_id: body.payment_id,
          payment_type: analytics?.payment_type,
          card_id: body.card_id ?? undefined,
          // num_items counts units ordered, items_count counts distinct lines.
          num_items: body.positions.reduce(
            (total, position) => total + position.amount,
            0,
          ),
          items_count: body.positions.length,
          source: "TELEGRAM",
        });
      }

      await queryClient.invalidateQueries({
        queryKey: activeOrdersCountQueryKey,
        refetchType: "all",
      });
      // Список заказов открывается прямо сейчас — его кэш обязан протухнуть,
      // иначе новый заказ появится в нём только после ручного обновления.
      // Префикс накрывает и ['orders'], и ['orders','paginated'].
      await queryClient.invalidateQueries({
        queryKey: ["orders"],
        refetchType: "all",
      });

      navigate("/profile/orders");
    },
    onError: (error: unknown) => {
      // Ответа нет совсем — нет сети (или запрос не дошёл до бэкенда).
      if (axios.isAxiosError(error) && !error.response) {
        custonSooner.error(t("no-network-title"));
        return;
      }
      // Отказ с текстом (например, превышен max_cart_qty) показываем как есть.
      const data = (error as { response?: { data?: { message?: string; title?: string } } })
        ?.response?.data;
      custonSooner.error(data?.message ?? data?.title ?? "Что то пошло не так");
    },
  });
  const createOrder = async (input: CreateOrderInput) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await mutateAsync(input);
    } catch {
      // Ошибку уже показал onError.
    } finally {
      inFlight.current = false;
    }
  };

  return {
    createOrder,
    isPending,
  };
};
